import { useEffect, useCallback, useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import _ from "lodash"

import config from "../../config"

import {
  fetchAppManifest,
  fetchMapLabels,
  fetchMapDecorations,
  fetchPagesContent,
} from "../../services/venue"
import { VenueContext } from "./VenueContext"
import { useSearch } from "./hooks/useSearch.ts"
import { useTheme } from "./hooks/useTheme.ts"

import { prepareGeolocationFeature } from "providers/geolocation/utils/prepareGeolocationFeature"
import {
  prepareMapDecorations,
  preparePagesContent,
} from "./utils/prepareFeature"

import {
  createGeolocationFeature,
  isLatLngQueryString,
} from "utils/geolocation"

import {
  prepareExtensionConfig,
  prepareFeatureQuickSearchConfig,
  prepareMapConfig,
  prepareSearchConfig,
  prepareStepAutoplayConfig,
  prepareIdleModeConfig,
  getIndexPathConfig,
  prepareVenueFeatureQuickSearchConfig,
  getDefaultRoutePathConfig,
  prepareVenueControllerConfig,
  setupVenueControllerConfig,
  prepareKeyboardConfig,
  preparePageConfig,
} from "./utils/prepareConfig"

import { useShortestPath } from "./modules/shortestPath/useShortestPath"
import { RealtimeProvider } from "./modules/realtime"
import {
  useGetImdf,
  useGetEvents,
  useGetPromotions,
  useGetAppConfig,
  useGetSponsoredContents,
  useGetProducts,
} from "./modules/data"

const LOCALE_CONFIG = {
  en: {
    localeKey: "en",
    name: "English",
    flagCode: "gb",
    localizedName: "English",
  },
  th: {
    localeKey: "th",
    name: "Thai",
    flagCode: "th",
    localizedName: "ไทย",
  },
  zh: {
    localeKey: "zh",
    name: "Chinese",
    flagCode: "cn",
    localizedName: "中文",
  },
  ja: {
    localeKey: "ja",
    name: "Japanese",
    flagCode: "jp",
    localizedName: "日本語",
  },
  ar: {
    localeKey: "ar",
    name: "Arabic",
    flagCode: "ar",
    localizedName: "العربية",
  },
}

const DEFAULT_LOCALE_SUPPORT_LIST = ["en", "th"]

export const defaultAppConfig = {
  map: {},
}

export const VenueProvider = ({ children }) => {
  // Modules
  /** State, Hooks */
  const device = config("device", "mobile")
  const deviceType = /^kiosk/.test(device) ? "kiosk" : "mobile"
  const isKiosk = /^kiosk/.test(deviceType)

  /** 
   * Example Extension Configs
    {
      "extensions": {
          "app": [
            {
              "to": "promotions", // <-- path for registering route
              "component": "tourist-friendly/pages/promotions" // <-- component path for loading
            },
            ....
          ]
        }
      }
    }
  */

  const { data: imdfData, isFetched, refetch: refetchImdfData } = useGetImdf()
  const { data: events } = useGetEvents({
    needLocation: !isKiosk,
  })
  const { data: promotions } = useGetPromotions({
    needLocation: !isKiosk,
  })
  const { data: products } = useGetProducts()

  const { data: appConfig, isFetched: isConfigFetched } = useGetAppConfig()

  const { data: sponsoredContent, isFetched: isSponsoredContentFetched } =
    useGetSponsoredContents({
      appConfig,
      imdfFeatures: imdfData?.features,
      promotions,
      events,
    })

  const mapConfig = useMemo(
    () => prepareMapConfig(_.get(appConfig, "map")),
    [appConfig]
  )

  const stepAutoplayConfig = useMemo(
    () => prepareStepAutoplayConfig(appConfig, deviceType),
    [appConfig, deviceType]
  )

  const pageConfig = useMemo(() => {
    return preparePageConfig(appConfig, deviceType)
  }, [appConfig, deviceType])

  const idleModeConfig = useMemo(
    () => prepareIdleModeConfig(appConfig, deviceType),
    [appConfig, deviceType]
  )

  const venueControllerConfig = useMemo(() => {
    const preparedConfig = prepareVenueControllerConfig(appConfig, deviceType)
    setupVenueControllerConfig(preparedConfig)
    return preparedConfig
  }, [appConfig, deviceType])

  const searchConfig = useMemo(
    () => prepareSearchConfig(appConfig, deviceType),
    [appConfig, deviceType]
  )

  const extensionConfig = useMemo(
    () => prepareExtensionConfig(appConfig, deviceType),
    [appConfig, deviceType]
  )

  const featureQuickSearchConfig = useMemo(
    () => prepareFeatureQuickSearchConfig(appConfig, deviceType),
    [appConfig, deviceType]
  )

  const venueFeatureQuickSearchConfig = useMemo(
    () => prepareVenueFeatureQuickSearchConfig(appConfig, deviceType),
    [appConfig, deviceType]
  )

  const keyboardConfig = useMemo(
    () => prepareKeyboardConfig(appConfig, deviceType),
    [appConfig, deviceType]
  )

  const { data: labels = [], isFetched: isLabelFetched } = useQuery({
    queryKey: ["labels"],
    queryFn: fetchMapLabels,
    retry: true,
    refetchOnWindowFocus: false,
  })

  const { data: decorations = [], isFetched: isDecorationFetched } = useQuery({
    queryKey: ["elements"],
    queryFn: async () => {
      const decorationsData = await fetchMapDecorations()
      return prepareMapDecorations(decorationsData)
    },
    retry: true,
    refetchOnWindowFocus: false,
  })

  const { data: appManifest } = useQuery({
    queryKey: ["app-manifest"],
    queryFn: fetchAppManifest,
    retry: true,
    refetchOnWindowFocus: false,
  })

  //correct locale properties is supported_locales
  const supportedLocales = useMemo(
    () =>
      _.get(
        _.first(imdfData.venues),
        "properties.supported_locales",
        DEFAULT_LOCALE_SUPPORT_LIST
      ),
    [imdfData.venues]
  )

  const locales = useMemo(() => {
    try {
      return _(supportedLocales)
        .map((locale) => _.get(LOCALE_CONFIG, locale))
        .compact()
        .value()
    } catch (error) {
      return _(DEFAULT_LOCALE_SUPPORT_LIST)
        .map((locale) => _.get(LOCALE_CONFIG, locale))
        .compact()
        .value()
    }
  }, [supportedLocales])

  const themeName = config("themeName")
  const mapThemeName = config("mapThemeName")
  const { themeOptions, initVenueTheme, initMapTheme, mapTheme } = useTheme(
    themeName,
    mapThemeName
  )

  const featuresToSearch = useMemo(() => {
    return _(imdfData)
      .pick(["occupants", "amenities", "sections"])
      .values()
      .flatten()
      .value()
  }, [imdfData])

  const { search, getSuggestedSearchItems } = useSearch(featuresToSearch, {
    config: searchConfig,
    enable: isConfigFetched,
  })

  const graphOptions = useMemo(
    () => _.get(mapConfig, "prepare_steps", {}),
    [mapConfig]
  )

  const {
    findRoute,
    findElevatorPreferredRoute,
    relationshipGraphLoaded,
    transformStepsToGeometries,
    normalizeStepInRoute,
  } = useShortestPath(imdfData, {
    /**
     * ==== Example App Config ====
     * "map": {
     *   ... other configs ...
     *   "shortest_path": {
     *     "resolution": 0.0016
     *   },
     *   "prepare_steps": {
     *     "traversal": {
     *       "room": 100,
     *       "unenclosedarea": 100
     *     },
     *     "escalator": {
     *       "baseDistance": 100,
     *       "multiplier": 1
     *     }
     *     // ...others...
     *   }
     * }
     */
    ..._.get(mapConfig, "shortest_path", {}),
    enabled: isFetched,
    graphOptions,
  })

  const rootCategories = useMemo(
    () =>
      _.uniqBy(
        _.filter(
          imdfData.categories,
          (feature) => feature.properties.parent_id === null
        ),
        "id"
      ),
    [imdfData.categories]
  )

  const findFeatureById = useCallback(
    (id) => imdfData.features.find((f) => f.id === id),
    [imdfData]
  )

  const filterOccupantsByName = useCallback(
    (name) => {
      return imdfData.occupants.filter(
        (occupant) => occupant.properties.name.en === name
      )
    },
    [imdfData]
  )

  const transformLocationToFeature = useCallback(
    (feature) => {
      return isLatLngQueryString(feature)
        ? prepareGeolocationFeature(
            createGeolocationFeature(feature, { name: "Your Location" }),
            {
              venues: imdfData.venues,
              levels: imdfData.levels,
              units: imdfData.units,
            }
          )
        : findFeatureById(feature) || null
    },
    [findFeatureById, imdfData.venues, imdfData.levels, imdfData.units]
  )

  const filterAmenitiesByCategory = useCallback(
    (category) => {
      return imdfData.amenities.filter(
        (amenityFeature) => amenityFeature.properties.category === category
      )
    },
    [imdfData.amenities]
  )

  const filterOccupantsByRootCategory = useCallback(
    (category) => {
      return imdfData.occupants.filter(
        ({ feature_type, properties }) =>
          feature_type === "occupant" &&
          properties.anchor &&
          (category === undefined ||
            properties.local_parent_categories?.[0]?.id === category)
      )
    },
    [imdfData.occupants]
  )

  const isMultiOrdinalVenue = useMemo(() => {
    return _.uniqBy(imdfData.levels, "properties.ordinal").length > 1
  }, [imdfData.levels])

  const isMultiVenueProject = useMemo(() => {
    return imdfData.venues.length > 1
  }, [imdfData.venues])

  // TODO: Find and Implement default Venue
  const defaultVenue = useMemo(
    () =>
      imdfData.venues.find(({ id }) => id === config("defaultVenueId")) ||
      _.first(imdfData.venues),
    [imdfData]
  )

  const indexPath = useMemo(
    () => getIndexPathConfig(appConfig, deviceType),
    [appConfig, deviceType]
  )

  const defaultRoutePath = useMemo(
    () => getDefaultRoutePathConfig(appConfig, deviceType),
    [appConfig, deviceType]
  )

  const { data: pagesContent, isFetched: isPagesContentFetched } = useQuery({
    queryKey: ["pagesContent"],
    queryFn: async () => {
      const pagesData = await fetchPagesContent()
      return preparePagesContent(pagesData)
    },
    retry: false,
    refetchOnWindowFocus: false,
  })

  const initVenue = useCallback(async () => {
    initVenueTheme()
    initMapTheme()
  }, [initVenueTheme, initMapTheme])

  /**
   * Side Effect when manifest is updated
   */
  useEffect(() => {
    // Set favicon
    const favicon = appManifest?.icons?.find(
      (icon) => icon.type === "image/x-icon"
    )
    if (favicon) {
      document.getElementById("favicon").href = favicon.src
    }
  }, [appManifest])

  // fetch ข้อมูลเริ่มต้นลง feature state และ theme
  useEffect(() => {
    try {
      initVenue()
    } catch (err) {
      throw err
    }
  }, [initVenue])

  const value = {
    ...imdfData,
    // Realtime
    events,
    promotions,
    products,
    labels,
    decorations,
    themeOptions,
    appManifest,
    mapTheme,
    locales,
    supportedLocales,
    localeConfig: LOCALE_CONFIG,
    rootCategories,
    findRoute,
    isMultiOrdinalVenue,
    isMultiVenueProject,
    findElevatorPreferredRoute,
    appConfig,
    stepAutoplayConfig,
    idleModeConfig,
    extensionConfig,
    sponsoredContent,
    defaultVenue,
    featureQuickSearchConfig,
    venueFeatureQuickSearchConfig,
    venueControllerConfig,
    keyboardConfig,
    pageConfig,
    pagesContent,
    // Flag
    configLoaded: isConfigFetched,
    relationshipGraphLoaded,
    themeLoaded: !!themeOptions,
    dataLoaded:
      isFetched &&
      isDecorationFetched &&
      isConfigFetched &&
      isSponsoredContentFetched &&
      isLabelFetched,
    isKiosk,
    isPagesContentFetched,
    mapConfig,
    indexPath,
    defaultRoutePath,
    deviceType,
    // functions
    refetchImdfData,
    findFeatureById,
    search,
    filterAmenitiesByCategory,
    filterOccupantsByName,
    filterOccupantsByRootCategory,
    transformLocationToFeature,
    transformStepsToGeometries,
    normalizeStepInRoute,
    getSuggestedSearchItems,
  }

  return (
    <VenueContext.Provider value={value}>
      <RealtimeProvider>{children}</RealtimeProvider>
    </VenueContext.Provider>
  )
}
